import { IIssueApi } from "@schemas/issueSchema";
import { ColDef, FirstDataRenderedEvent, GridReadyEvent } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "ag-grid-enterprise";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { usePutProjectByProjectIdIssueUpdateSimpleMutation } from "../../../../../../openapi";
import { AG_GRID_LOCALE_NOR } from "../../../../../agGridLocale";
import { useAgGridReduxState } from "../../../../../hooks/useAgGridReduxState";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import { DEFAULT_VISIBLE_ISSUE_COLUMNS, ISSUE_DATAFILEDS_ENUM } from "../../issueConstants";
import { useDoUpdateIssueMutation } from "../../issueRTKLegacy";
import { selectActiveTableTemplate } from "../../issueSelectors";
import { issueBoardHasClientAccess } from "../../issueUtils";
import { selectClickedRoomNumberInModal } from "../Floorplan/floorplanSelectors";
import { setClickedRoomNumberInModalInRedux } from "../Floorplan/floorplanSlice";
import { buildIssueColumnGroups } from "./columns/issueColumnGroups";
import {
  createDateColumnEditor as createDateColumnEditorFactory,
  createHideAndSort,
  createIssueColumnBaseDefGetter,
  createUserColumnEditor as createUserColumnEditorFactory,
} from "./columns/issueColumnHelpers";
import { getIssueBaseColumns } from "./columns/issueBaseColumns";
import { getAgGridOtherAttributeColumns as buildOtherAttributeColumns } from "./columns/issueOtherAttributeColumns";
import { getRisikoEvaluationColumns as buildRisikoEvaluationColumns } from "./columns/issueRisikoColumns";
import { IssueTableColumnContext } from "./columns/issueTableColumnContext";
import IssueTableRoomViewerModal from "./IssueTableRoomViewerModal";
import IssueTableDetailCellRenderer from "./IssueTableDetailCellRenderer";
import {
  defaultIssueTableColDef,
  ISSUE_TABLE_HEADER_HEIGHT,
  ISSUE_TABLE_PAGE_SIZE,
  ISSUE_TABLE_PAGE_SIZE_OPTIONS,
  ISSUE_TABLE_ROW_BUFFER,
  ISSUE_TABLE_ROW_HEIGHT,
  issueTableColumnTypes,
} from "./issueTableGridConfig";
import { IIssueTableAgGridColumnProps } from "./issueTableTypes";
import { useIssueTableCellEditing } from "./useIssueTableCellEditing";
import { useIssueTableColumnStateHandlers } from "./useIssueTableColumnStateHandlers";
import { useIssueTableSidebar } from "./useIssueTableSidebar";

const IssueTableAgGridRefactored: React.FC<{
  items: IIssueApi[];
  columnProps: IIssueTableAgGridColumnProps;
  loading: boolean;
  gridRef: React.RefObject<AgGridReact>;
}> = ({ items, columnProps, gridRef }) => {
  const {
    boardBuckets,
    setSelectedIssue,
    types,
    pnsItems,
    members,
    issueBoardId,
    projectId,
    updateItemMembers,
    otherAttributesConfig,
    otherAttributesTasksConfig,
    issueBoard,
  } = columnProps;
  let refContainer = useRef(null);
  const allColumnState = useAppSelector((state) => state.issueReducerNew.allColumnState);
  const activeTableTemplate = useAppSelector(selectActiveTableTemplate);
  const clickedRoomNumberInModal = useAppSelector(selectClickedRoomNumberInModal);
  const [updateIssue] = useDoUpdateIssueMutation();
  const [updateIssueSimple] = usePutProjectByProjectIdIssueUpdateSimpleMutation();
  const dispatch = useAppDispatch();
  const boardHasClientAccess = issueBoardHasClientAccess(issueBoard);
  const columnsVisibility =
    (allColumnState[issueBoardId]?.length > 0
      ? allColumnState[issueBoardId]?.map((col) => col.colId)
      : DEFAULT_VISIBLE_ISSUE_COLUMNS) ?? DEFAULT_VISIBLE_ISSUE_COLUMNS; // use default cols if template is empty, or no state
  const hideAndSort = useMemo(
    () => createHideAndSort({ activeTableTemplate, columnsVisibility, issueBoard }),
    [activeTableTemplate, columnsVisibility, issueBoard],
  );

  const closeRoomViewerModal = useCallback(() => {
    dispatch(setClickedRoomNumberInModalInRedux({ issueBoardId, clickedRoomNumberInModal: null }));
  }, [dispatch, issueBoardId]);

  useEffect(() => {
    return () => {
      dispatch(setClickedRoomNumberInModalInRedux({ issueBoardId, clickedRoomNumberInModal: null }));
    };
  }, [dispatch, issueBoardId]);

  const getIssueColumnBaseDef = useMemo(() => createIssueColumnBaseDefGetter(hideAndSort), [hideAndSort]);
  const createDateColumnEditor = useMemo(() => createDateColumnEditorFactory(), []);
  const createUserColumnEditor = useMemo(() => createUserColumnEditorFactory(members), [members]);

  const { handleToolPanelVisibleChanged, icons, sideBar } = useIssueTableSidebar();

  const handleUpdateIssueField = useCallback(
    async (issueId: string, attr: string, value: any, oldValue: any = "") => {
      await updateIssue({
        projectId,
        issueBoardId,
        issueId,
        attr,
        value,
        oldValue,
      });
    },
    [projectId, issueBoardId, updateIssue],
  );

  const columnContext: IssueTableColumnContext = useMemo(
    () => ({
      boardBuckets,
      boardHasClientAccess,
      createDateColumnEditor,
      createUserColumnEditor,
      getIssueColumnBaseDef,
      handleUpdateIssueField,
      hideAndSort,
      issueBoard,
      issueBoardId,
      members,
      pnsItems,
      projectId,
      setSelectedIssue,
      types,
      updateItemMembers,
    }),
    [
      boardBuckets,
      boardHasClientAccess,
      createDateColumnEditor,
      createUserColumnEditor,
      getIssueColumnBaseDef,
      handleUpdateIssueField,
      hideAndSort,
      issueBoard,
      issueBoardId,
      members,
      pnsItems,
      projectId,
      setSelectedIssue,
      types,
      updateItemMembers,
    ],
  );

  const columns: ColDef<IIssueApi>[] = useMemo(
    () => getIssueBaseColumns(columnContext),
    [columnContext],
  );
  const otherAttributeColumns = useMemo(
    () =>
      buildOtherAttributeColumns({
        columnContext,
        otherAttributesConfig,
      }),
    [columnContext, otherAttributesConfig],
  );
  const risikoColumns = useMemo(
    () =>
      issueBoard?.risikoSection?.isActive
        ? buildRisikoEvaluationColumns({
            columnContext,
          })
        : [],
    [columnContext, issueBoard?.risikoSection?.isActive],
  );

  const groupedColumns: ColDef<IIssueApi>[] = useMemo(
    () =>
      buildIssueColumnGroups({
        columns: [...columns, ...risikoColumns],
        columnsVisibility,
        issueBoard,
        otherAttributeColumns,
      }),
    [columns, columnsVisibility, issueBoard, otherAttributeColumns, risikoColumns],
  );

  const defaultColDef = defaultIssueTableColDef;
  const { debouncedHandleToggleColumnVisibility, onColumnResized, onDragStarted, onDragStopped, onSortChanged } =
    useIssueTableColumnStateHandlers();
  const onCellEditRequest = useIssueTableCellEditing({
    issueBoardId,
    projectId,
    updateIssue,
    updateIssueSimple,
  });
  const gridStateKey = `issue-table-${issueBoardId}`;
  const { onGridReady: onGenericGridReady, onStateUpdated } = useAgGridReduxState<IIssueApi>(
    gridStateKey,
    undefined,
    { autoSizeOnFirstReady: false },
  );
  const onGridReady = useCallback(
    (event: GridReadyEvent<IIssueApi>) => {
      onGenericGridReady(event);
      allColumnState[issueBoardId]?.forEach((col) => {
        if (col?.orderPriority != null) {
          event.api.moveColumns([col.colId], col.orderPriority);
        }
        if (col?.width != null) {
          event.api.setColumnWidths([{ key: col.colId, newWidth: col.width }]);
        }
      });
    },
    [allColumnState, issueBoardId, onGenericGridReady],
  );
  const onFirstDataRendered = useCallback(
    (event: FirstDataRenderedEvent<IIssueApi>) => {
      const hasAnyTask = items?.some((item) => item.connectedTasks?.length > 0) ?? false;
      const idColumnConfig = allColumnState[issueBoardId]?.find((col) => col.colId === ISSUE_DATAFILEDS_ENUM.ID);

      if (idColumnConfig?.width == null) {
        event.api.setColumnWidths([{ key: ISSUE_DATAFILEDS_ENUM.ID, newWidth: hasAnyTask ? 140 : 100 }]);
      }
    },
    [allColumnState, issueBoardId, items],
  );
  const DetailCellRenderer = useCallback(
    (props: CustomCellRendererProps<IIssueApi>) => (
      <IssueTableDetailCellRenderer
        baseColumns={columns}
        columnContext={columnContext}
        data={props.data}
        defaultColDef={defaultColDef}
        onCellEditRequest={onCellEditRequest}
        otherAttributesTasksConfig={otherAttributesTasksConfig}
      />
    ),
    [columns, columnContext, defaultColDef, onCellEditRequest, otherAttributesTasksConfig],
  );

  return (
    <div className="d-flex flex-column w-100 h-100">
      <IssueTableRoomViewerModal opened={clickedRoomNumberInModal != null} onClose={closeRoomViewerModal} />

      <div className=" ag-theme-quartz h-100" id="myGrid" ref={refContainer}>
        <AgGridReact
          onDragStarted={onDragStarted}
          onDragStopped={onDragStopped}
          onColumnVisible={debouncedHandleToggleColumnVisibility}
          onColumnResized={onColumnResized}
          suppressHeaderFocus={true}
          cellSelection={true}
          rowHeight={ISSUE_TABLE_ROW_HEIGHT}
          headerHeight={ISSUE_TABLE_HEADER_HEIGHT}
          columnTypes={issueTableColumnTypes}
          maintainColumnOrder={true}
          ref={gridRef}
          getRowId={(p) => p.data._id}
          rowData={items}
          columnDefs={groupedColumns}
          stopEditingWhenCellsLoseFocus={true}
          readOnlyEdit={true}
          onCellEditRequest={onCellEditRequest}
          defaultColDef={defaultColDef}
          animateRows={false}
          icons={icons}
          onGridReady={onGridReady}
          onFirstDataRendered={onFirstDataRendered}
          onStateUpdated={onStateUpdated}
          sideBar={sideBar}
          pagination={true}
          paginationPageSize={ISSUE_TABLE_PAGE_SIZE}
          paginationPageSizeSelector={ISSUE_TABLE_PAGE_SIZE_OPTIONS}
          suppressColumnVirtualisation={columnsVisibility.length < 25}
          suppressRowVirtualisation={items?.length < 100}
          rowBuffer={ISSUE_TABLE_ROW_BUFFER}
          masterDetail={true}
          detailRowAutoHeight={true}
          isRowMaster={(data) => data?.taskOrIssue === "ISSUE"}
          detailCellRenderer={DetailCellRenderer}
          localeText={AG_GRID_LOCALE_NOR}
          onToolPanelVisibleChanged={handleToolPanelVisibleChanged}
          onSortChanged={(e) => onSortChanged(e)}
          suppressCutToClipboard={true}
        />
      </div>
    </div>
  );
};

export default IssueTableAgGridRefactored;
